<template>
  <ProSpaceGeoLayout class="addressprogramorder" v-if="model">
    <template #top>
      <ProSpaceTopPanel :header="layoutName" back @back="backHandler">
        <template #center>
          <ProSpaceTag
            v-if="headerTag && Object.keys(headerTag).length && !this.isMultiAdjustmentMode"
            :text="headerTag.text"
            :type="headerTag.type"
            :prefix="headerTag.prefix"
            bordered
          />
        </template>
        <template #right>
          <ProSpaceAction
            v-if="!adjustmentMode"
            ref="action-saveAndClose"
            icon="double-check"
            :text="$t('saveAndClose')"
            :disabled="v$.model.$invalid || loading || mixinLoadingAccess"
            @click="saveAndClose"
          />
          <ProSpaceAction
            v-if="adjustmentMode && checkAccess('action-sendAndClose', null, true)"
            ref="action-sendAndClose"
            icon="double-check"
            :text="$t('sendAndClose')"
            :disabled="v$.model.$invalid || !isCorrectionChanges || loading || mixinLoadingAccess"
            @click="sendAndClose"
          />
        </template>
      </ProSpaceTopPanel>
    </template>
    <template #center>
      <ProSpaceGeoLayout
        :is-templated="true"
        :template-center="true"
        :template-right="true"
        :class="{ 'addressprogramorder__outlets': modelTab.systemName === 'outlets' }"
      >
        <template #center>
          <ProSpaceTabs
            v-model="modelTab"
            :tabs="overviewTabs"
            :loading="loading"
            :refLoading="getRefLoading"
            @select-tab="onSelectTab"
          >
            <ProSpaceHLayout :gap="10">
              <ProSpaceAction
                v-if="!adjustmentMode && orderStatus !== 'completed' && needCheckOrderStatusForMountAccess"
                ref="action-adjustmentRequest"
                icon="list-check"
                :text="$t('adjustmentRequest')"
                @click="toggleAdjustmentMode"
              />
              <ProSpaceAction
                ref="action-cancel"
                icon=""
                :text="$t('cancelTxt')"
                @click="cancelHandler"
              />
              <ProSpaceAction
                v-if="!adjustmentMode"
                ref="action-save"
                icon="check"
                :text="$t('save')"
                :disabled="v$.model.$invalid"
                @click="save()"
              />
            </ProSpaceHLayout>
            <template v-slot:tab-1>
              <ProSpaceVLayout class="addressprogramorder__rows">
                <ProSpaceGeoLayout
                  class="geo-row"
                  isHideBottom
                  isHideTop
                  :isHideRight="
                    !v$.model.networkId.$error &&
                    !v$.model.address.$error &&
                    !v$.model.coverage.$error
                  "
                >
                  <template v-slot:left>
                    <ProSpaceTextBlock
                      :top="$t('addressProgramOutletInfo')"
                      :bottom="$t('addressProgramOutletDescription')"
                      :gap="10"
                      break-word
                    />
                  </template>
                  <template v-slot:center>
                    <ProSpaceHLayout
                      justify-content="flex-start"
                      align-items="flex-start"
                      :gap="20"
                    >
                      <ProSpaceSearchfield
                        ref="networkId-ProSpaceSearchfield"
                        v-model="model.networkId"
                        getMethod="grid"
                        optionValue="id"
                        optionLabel="value"
                        filterModel="value"
                        :displayValue="model.networkValue"
                        :label="$t('addressProgramOrderNetworkValue')"
                        :txtTitleModal="$t('addressProgramOrderNetworkValue')"
                        :searchPlaceholder="
                          adjustmentFields.includes('networkId')
                            ? $t('addressProgramOrderAdjustmentNewValue')
                            : $t('addressProgramOrderNetworkValue')
                        "
                        :applyTxt="$t('applyTxt')"
                        :cancelTxt="$t('cancelTxt')"
                        :service="networkService"
                        :columns="networkColumns"
                        :fastFilters="[]"
                        :defaultFilter="{ disabled: { and: [false] } }"
                        :invalid="v$.model.networkId.$error"
                        :invalid-messages="v$.model.networkId.$errors"
                        :grid-styles="searchFieldGridStyles"
                        @blur="v$.model.networkId.$touch"
                        @update:modelValue="
                          changeModelValue('networkValue', 'networkId')
                        "
                      />
                      <ProSpaceInputText
                        ref="address-ProSpaceInputText"
                        v-model="model.address"
                        :label="$t('addressProgramOrderAddress')"
                        :placeholder="
                          adjustmentFields.includes('address')
                            ? $t('addressProgramOrderAdjustmentNewValue')
                            : $t('addressProgramOrderAddress')
                        "
                        :invalid="v$.model.address.$error"
                        :invalid-messages="v$.model.address.$errors"
                        @blur="v$.model.address.$touch"
                      />
                      <ProSpaceDropdown ref="coverage-ProSpaceInputSwitch"
                        v-model="model.coverage"
                        :options="coverageOptions"
                        propLabel="label"
                        propValue="value"
                        :placeholder="$t('selectValue')"
                        :label="$t('coverage')"
                      />
                    </ProSpaceHLayout>
                  </template>
                  <template
                    v-if="
                      v$.model.networkId.$error ||
                      v$.model.address.$error ||
                      v$.model.coverage.$error
                    "
                    v-slot:right
                  >
                    <ProSpaceVLayout justify-content="center">
                      <ProSpaceIcon icon="warning" colored />
                    </ProSpaceVLayout>
                  </template>
                </ProSpaceGeoLayout>
                <ProSpaceGeoLayout
                  class="geo-row"
                  isHideBottom
                  isHideTop
                  :isHideRight="
                    !v$.model.area.$error &&
                    !v$.model.city.$error &&
                    !v$.model.clusterId.$error &&
                    !v$.model.hsrId.$error &&
                    !v$.model.tdmId.$error &&
                    !v$.model.kadlId.$error
                  "
                >
                  <template v-slot:left>
                    <ProSpaceTextBlock
                      :top="$t('addressProgramGeoPointInfo')"
                      :bottom="$t('addressProgramGeoPointDescription')"
                      :gap="10"
                      break-word
                    />
                  </template>
                  <template v-slot:center>
                    <ProSpaceVLayout :gap="20">
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceInputText
                          ref="area-ProSpaceInputText"
                          v-model="model.area"
                          :label="$t('addressProgramOrderArea')"
                          :placeholder="
                            adjustmentFields.includes('area')
                              ? $t('addressProgramOrderAdjustmentNewValue')
                              : $t('addressProgramOrderArea')
                          "
                          :invalid="v$.model.area.$error"
                          :invalid-messages="v$.model.area.$errors"
                          @blur="v$.model.area.$touch"
                        />
                        <ProSpaceInputText
                          ref="city-ProSpaceInputText"
                          v-model="model.city"
                          :label="$t('addressProgramOrderCity')"
                          :placeholder="
                            adjustmentFields.includes('city')
                              ? $t('addressProgramOrderAdjustmentNewValue')
                              : $t('addressProgramOrderCity')
                          "
                          :invalid="v$.model.city.$error"
                          :invalid-messages="v$.model.city.$errors"
                          @blur="v$.model.city.$touch"
                        />
                        <ProSpaceSearchfield
                          ref="clusterId-ProSpaceSearchfield"
                          v-model="model.clusterId"
                          getMethod="grid"
                          optionValue="id"
                          optionLabel="value"
                          filterModel="value"
                          :displayValue="model.clusterValue"
                          :label="$t('addressProgramOrderClusterValue')"
                          :txtTitleModal="$t('addressProgramOrderClusterValue')"
                          :searchPlaceholder="
                            adjustmentFields.includes('clusterId')
                              ? $t('addressProgramOrderAdjustmentNewValue')
                              : $t('addressProgramOrderClusterValue')
                          "
                          :applyTxt="$t('applyTxt')"
                          :cancelTxt="$t('cancelTxt')"
                          :service="clusterService"
                          :columns="clusterColumns"
                          :fastFilters="[]"
                          :defaultFilter="{ disabled: { and: [false] } }"
                          :invalid="v$.model.clusterId.$error"
                          :invalid-messages="v$.model.clusterId.$errors"
                          :grid-styles="searchFieldGridStyles"
                          @blur="v$.model.clusterId.$touch"
                          @update:modelValue="
                            changeModelValue('clusterValue', 'clusterId')
                          "
                        />
                      </ProSpaceHLayout>
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceSearchfield
                          ref="hsrId-ProSpaceSearchfield"
                          v-model="model.hsrId"
                          getMethod="grid"
                          optionValue="id"
                          optionLabel="value"
                          filterModel="value"
                          :displayValue="model.hsrValue"
                          :label="$t('addressProgramOrderHsrValue')"
                          :txtTitleModal="$t('addressProgramOrderHsrValue')"
                          :searchPlaceholder="
                            adjustmentFields.includes('hsrId')
                              ? $t('addressProgramOrderAdjustmentNewValue')
                              : $t('addressProgramOrderHsrValue')
                          "
                          :applyTxt="$t('applyTxt')"
                          :cancelTxt="$t('cancelTxt')"
                          :service="hSRService"
                          :columns="hSRColumns"
                          :fastFilters="[]"
                          :defaultFilter="{ disabled: { and: [false] } }"
                          :invalid="v$.model.hsrId.$error"
                          :invalid-messages="v$.model.hsrId.$errors"
                          :grid-styles="searchFieldGridStyles"
                          @blur="v$.model.hsrId.$touch"
                          @update:modelValue="
                            changeModelValue('hsrValue', 'hsrId')
                          "
                        />
                        <ProSpaceSearchfield
                          ref="tdmId-ProSpaceSearchfield"
                          v-model="model.tdmId"
                          getMethod="grid"
                          optionValue="id"
                          optionLabel="value"
                          filterModel="value"
                          :displayValue="model.tdmValue"
                          :label="$t('addressProgramOrderTdmValue')"
                          :txtTitleModal="$t('addressProgramOrderTdmValue')"
                          :searchPlaceholder="
                            adjustmentFields.includes('tdmId')
                              ? $t('addressProgramOrderAdjustmentNewValue')
                              : $t('addressProgramOrderTdmValue')
                          "
                          :applyTxt="$t('applyTxt')"
                          :cancelTxt="$t('cancelTxt')"
                          :service="tDMService"
                          :columns="tDMColumns"
                          :fastFilters="[]"
                          :defaultFilter="{ disabled: { and: [false] } }"
                          :invalid="v$.model.tdmId.$error"
                          :invalid-messages="v$.model.tdmId.$errors"
                          :grid-styles="searchFieldGridStyles"
                          @blur="v$.model.tdmId.$touch"
                          @update:modelValue="
                            changeModelValue('tdmValue', 'tdmId')
                          "
                        />
                        <ProSpaceSearchfield
                          ref="kadlId-ProSpaceSearchfield"
                          v-model="model.kadlId"
                          getMethod="grid"
                          optionValue="id"
                          optionLabel="value"
                          filterModel="value"
                          :displayValue="model.kadlValue"
                          :label="$t('addressProgramOrderKadlValue')"
                          :txtTitleModal="$t('addressProgramOrderKadlValue')"
                          :searchPlaceholder="
                            adjustmentFields.includes('kadlId')
                              ? $t('addressProgramOrderAdjustmentNewValue')
                              : $t('addressProgramOrderKadlValue')
                          "
                          :applyTxt="$t('applyTxt')"
                          :cancelTxt="$t('cancelTxt')"
                          :service="kADLService"
                          :columns="kADLColumns"
                          :fastFilters="[]"
                          :defaultFilter="{ disabled: { and: [false] } }"
                          :invalid="v$.model.kadlId.$error"
                          :invalid-messages="v$.model.kadlId.$errors"
                          :grid-styles="searchFieldGridStyles"
                          @blur="v$.model.kadlId.$touch"
                          @update:modelValue="
                            changeModelValue('kadlValue', 'kadlId')
                          "
                        />
                      </ProSpaceHLayout>
                    </ProSpaceVLayout>
                  </template>

                  <template
                    v-if="
                      v$.model.area.$error ||
                      v$.model.city.$error ||
                      v$.model.clusterId.$error ||
                      v$.model.hsrId.$error ||
                      v$.model.tdmId.$error ||
                      v$.model.kadlId.$error
                    "
                    v-slot:right
                  >
                    <ProSpaceVLayout justify-content="center">
                      <ProSpaceIcon icon="warning" colored />
                    </ProSpaceVLayout>
                  </template>
                </ProSpaceGeoLayout>
                <ProSpaceGeoLayout
                  class="geo-row"
                  isHideBottom
                  isHideTop
                  :isHideRight="
                    !v$.model.externalFormatId.$error &&
                    !v$.model.outletTypeId.$error &&
                    !v$.model.channelId.$error &&
                    !v$.model.narkaId.$error &&
                    !v$.model.sipChannelId.$error &&
                    !v$.model.territoryCluster.$error &&
                    !v$.model.externalCode.$error &&
                    !v$.model.tariffZone.$error &&
                    !v$.model.skyline.$error
                  "
                >
                  <template v-slot:left>
                    <ProSpaceTextBlock
                      :top="$t('addressProgramClassifiersParamsInfo')"
                      :bottom="$t('addressProgramClassifiersParamsDescription')"
                      :gap="10"
                      break-word
                    />
                  </template>
                  <template v-slot:center>
                    <ProSpaceVLayout :gap="20">
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceSearchfield
                          ref="externalFormatId-ProSpaceSearchfield"
                          v-model="model.externalFormatId"
                          getMethod="grid"
                          optionValue="id"
                          optionLabel="value"
                          filterModel="value"
                          :displayValue="model.externalFormatValue"
                          :label="$t('addressProgramOrderExternalFormatValue')"
                          :txtTitleModal="$t('addressProgramOrderExternalFormatValue')"
                          :searchPlaceholder="
                            adjustmentFields.includes('externalFormatId')
                              ? $t('addressProgramOrderAdjustmentNewValue')
                              : $t('addressProgramOrderExternalFormatValue')
                          "
                          :applyTxt="$t('applyTxt')"
                          :cancelTxt="$t('cancelTxt')"
                          :service="externalFormatService"
                          :columns="externalFormatColumns"
                          :fastFilters="[]"
                          :defaultFilter="{ disabled: { and: [false] } }"
                          :invalid="v$.model.externalFormatId.$error"
                          :invalid-messages="v$.model.externalFormatId.$errors"
                          :grid-styles="searchFieldGridStyles"
                          @blur="v$.model.externalFormatId.$touch"
                          @update:modelValue="
                            changeModelValue(
                              'externalFormatValue',
                              'externalFormatId'
                            )
                          "
                        />
                        <ProSpaceSearchfield
                          ref="outletTypeId-ProSpaceSearchfield"
                          v-model="model.outletTypeId"
                          getMethod="grid"
                          optionValue="id"
                          optionLabel="value"
                          filterModel="value"
                          :displayValue="model.outletTypeValue"
                          :label="$t('addressProgramOrderOutletTypeValue')"
                          :txtTitleModal="$t('addressProgramOrderOutletTypeValue')"
                          :searchPlaceholder="
                            adjustmentFields.includes('outletTypeId')
                              ? $t('addressProgramOrderAdjustmentNewValue')
                              : $t('addressProgramOrderOutletTypeValue')
                          "
                          :applyTxt="$t('applyTxt')"
                          :cancelTxt="$t('cancelTxt')"
                          :service="outletTypeService"
                          :columns="outletTypeColumns"
                          :fastFilters="[]"
                          :defaultFilter="{ disabled: { and: [false] } }"
                          :invalid="v$.model.outletTypeId.$error"
                          :invalid-messages="v$.model.outletTypeId.$errors"
                          :grid-styles="searchFieldGridStyles"
                          @blur="v$.model.outletTypeId.$touch"
                          @update:modelValue="
                            changeModelValue('outletTypeValue', 'outletTypeId')
                          "
                        />
                        <ProSpaceSearchfield
                          ref="channelId-ProSpaceSearchfield"
                          v-model="model.channelId"
                          getMethod="grid"
                          optionValue="id"
                          optionLabel="value"
                          filterModel="value"
                          :displayValue="model.channelValue"
                          :label="$t('addressProgramOrderChannelValue')"
                          :txtTitleModal="$t('addressProgramOrderChannelValue')"
                          :searchPlaceholder="
                            adjustmentFields.includes('channelId')
                              ? $t('addressProgramOrderAdjustmentNewValue')
                              : $t('addressProgramOrderChannelValue')
                          "
                          :applyTxt="$t('applyTxt')"
                          :cancelTxt="$t('cancelTxt')"
                          :service="channelService"
                          :columns="channelColumns"
                          :fastFilters="[]"
                          :defaultFilter="{ disabled: { and: [false] } }"
                          :invalid="v$.model.channelId.$error"
                          :invalid-messages="v$.model.channelId.$errors"
                          :grid-styles="searchFieldGridStyles"
                          @blur="v$.model.channelId.$touch"
                          @update:modelValue="
                            changeModelValue('channelValue', 'channelId')
                          "
                        />
                      </ProSpaceHLayout>
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceSearchfield
                          ref="narkaId-ProSpaceSearchfield"
                          v-model="model.narkaId"
                          getMethod="grid"
                          optionValue="id"
                          optionLabel="value"
                          filterModel="value"
                          :displayValue="model.narkaValue"
                          :label="$t('addressProgramOrderNarkaValue')"
                          :txtTitleModal="$t('addressProgramOrderNarkaValue')"
                          :searchPlaceholder="
                            adjustmentFields.includes('narkaId')
                              ? $t('addressProgramOrderAdjustmentNewValue')
                              : $t('addressProgramOrderNarkaValue')
                          "
                          :applyTxt="$t('applyTxt')"
                          :cancelTxt="$t('cancelTxt')"
                          :service="nARKAService"
                          :columns="nARKAColumns"
                          :fastFilters="[]"
                          :defaultFilter="{ disabled: { and: [false] } }"
                          :invalid="v$.model.narkaId.$error"
                          :invalid-messages="v$.model.narkaId.$errors"
                          :grid-styles="searchFieldGridStyles"
                          @blur="v$.model.narkaId.$touch"
                          @update:modelValue="
                            changeModelValue('narkaValue', 'narkaId')
                          "
                        />
                        <ProSpaceSearchfield
                          ref="sipChannelId-ProSpaceSearchfield"
                          v-model="model.sipChannelId"
                          getMethod="grid"
                          optionValue="id"
                          optionLabel="value"
                          filterModel="value"
                          :displayValue="model.sipChannelValue"
                          :label="$t('addressProgramOrderSipChannelValue')"
                          :txtTitleModal="$t('addressProgramOrderSipChannelValue')"
                          :searchPlaceholder="
                            adjustmentFields.includes('sipChannelId')
                              ? $t('addressProgramOrderAdjustmentNewValue')
                              : $t('addressProgramOrderSipChannelValue')
                          "
                          :applyTxt="$t('applyTxt')"
                          :cancelTxt="$t('cancelTxt')"
                          :service="sIPChannelService"
                          :columns="sIPChannelColumns"
                          :fastFilters="[]"
                          :defaultFilter="{ disabled: { and: [false] } }"
                          :invalid="v$.model.sipChannelId.$error"
                          :invalid-messages="v$.model.sipChannelId.$errors"
                          :grid-styles="searchFieldGridStyles"
                          @blur="v$.model.sipChannelId.$touch"
                          @update:modelValue="
                            changeModelValue('sipChannelValue', 'sipChannelId')
                          "
                        />
                        <ProSpaceInputNumber
                          ref="territoryCluster-ProSpaceInputNumber"
                          v-model="model.territoryCluster"
                          :label="$t('addressProgramOrderTerritoryCluster')"
                          :placeholder="
                            adjustmentFields.includes('territoryCluster')
                              ? $t('addressProgramOrderAdjustmentNewValue')
                              : $t('addressProgramOrderTerritoryCluster')
                          "
                          :invalid="v$.model.territoryCluster.$error"
                          :invalid-messages="v$.model.territoryCluster.$errors"
                          likeText
                          @blur="v$.model.territoryCluster.$touch"
                        />
                      </ProSpaceHLayout>
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                        style="display: grid; grid-template-columns: repeat(3, 1fr);"
                      >
                        <ProSpaceInputText
                          ref="skyline-ProSpaceInputNumber"
                          v-model="model.skyline"
                          :label="$t('addressProgramOrderSkyline')"
                          :placeholder="
                            adjustmentFields.includes('skyline')
                              ? $t('addressProgramOrderAdjustmentNewValue')
                              : $t('addressProgramOrderSkyline')
                          "
                          :invalid="v$.model.skyline.$error"
                          :invalid-messages="v$.model.skyline.$errors"
                          likeText
                          @blur="v$.model.skyline.$touch"
                        />
                        <ProSpaceInputText
                          ref="externalCode-ProSpaceInputText"
                          v-model="model.externalCode"
                          :label="$t('addressProgramOrderExternalCode')"
                          :placeholder="
                            adjustmentFields.includes('externalCode')
                              ? $t('addressProgramOrderAdjustmentNewValue')
                              : $t('addressProgramOrderExternalCode')
                          "
                          :invalid="v$.model.externalCode.$error"
                          :invalid-messages="v$.model.externalCode.$errors"
                          likeText
                          @blur="v$.model.externalCode.$touch"
                        />
                        <ProSpaceInputNumber
                          ref="tariffZone-ProSpaceInputNumber"
                          v-model="model.tariffZone"
                          :label="$t('addressProgramOrderTariffZone')"
                          :placeholder="
                            adjustmentFields.includes('tariffZone')
                              ? $t('addressProgramOrderAdjustmentNewValue')
                              : $t('addressProgramOrderTariffZone')
                          "
                          :invalid="v$.model.tariffZone.$error"
                          :invalid-messages="v$.model.tariffZone.$errors"
                          likeText
                          @blur="v$.model.tariffZone.$touch"
                        />
                      </ProSpaceHLayout>
                    </ProSpaceVLayout>
                  </template>

                  <template
                    v-if="
                      v$.model.externalFormatId.$error ||
                      v$.model.outletTypeId.$error ||
                      v$.model.channelId.$error ||
                      v$.model.narkaId.$error ||
                      v$.model.sipChannelId.$error ||
                      v$.model.territoryCluster.$error ||
                      v$.model.externalCode.$error ||
                      v$.model.tariffZone.$error ||
                      v$.model.skyline.$error
                    "
                    v-slot:right
                  >
                    <ProSpaceVLayout justify-content="center">
                      <ProSpaceIcon icon="warning" colored />
                    </ProSpaceVLayout>
                  </template>
                </ProSpaceGeoLayout>
                <ProSpaceGeoLayout
                  class="geo-row"
                  isHideBottom
                  isHideTop
                  :isHideRight="
                    !v$.model.kasValue.$error &&
                    !v$.model.merchandiserValue.$error &&
                    !v$.model.routeCodeValue.$error
                  "
                >
                  <template v-slot:left>
                    <ProSpaceTextBlock
                      :top="$t('addressProgramAgencyParamsInfo')"
                      :bottom="$t('addressProgramAgencyParamsDescription')"
                      :gap="10"
                      break-word
                    />
                  </template>
                  <template v-slot:center>
                    <ProSpaceVLayout :gap="20">
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                        style="display: grid; grid-template-columns: repeat(3, 1fr);"
                      >
                        <ProSpaceSearchfield
                          v-if="!adjustmentMode"
                          ref="agencyId-ProSpaceSearchfield"
                          v-model="model.agencyId"
                          getMethod="grid"
                          optionValue="id"
                          optionLabel="value"
                          filterModel="value"
                          :displayValue="model.agencyValue"
                          :label="$t('addressProgramOrderAgencyValue')"
                          :txtTitleModal="$t('addressProgramOrderAgencyValue')"
                          :searchPlaceholder="
                            $t('addressProgramOrderAgencyValue')
                          "
                          :applyTxt="$t('applyTxt')"
                          :cancelTxt="$t('cancelTxt')"
                          :service="agencyService"
                          :columns="agencyColumns"
                          :fastFilters="[]"
                          :defaultFilter="{ disabled: { and: [false] } }"
                          :invalid="v$.model.agencyId.$error"
                          :invalid-messages="v$.model.agencyId.$errors"
                          :grid-styles="searchFieldGridStyles"
                          @blur="v$.model.agencyId.$touch"
                        />
                      </ProSpaceHLayout>
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceSearchfield
                          ref="kasValue-ProSpaceSearchfield"
                          v-model="model.kasValue"
                          getMethod="grid"
                          optionValue="value"
                          optionLabel="value"
                          filterModel="value"
                          :displayValue="model.kasValue"
                          :label="$t('addressProgramOrderKasValue')"
                          :txtTitleModal="$t('addressProgramOrderKasValue')"
                          :searchPlaceholder="
                            adjustmentFields.includes('kasValue')
                              ? $t('addressProgramOrderAdjustmentNewValue')
                              : $t('addressProgramOrderKasValue')
                          "
                          :applyTxt="$t('applyTxt')"
                          :cancelTxt="$t('cancelTxt')"
                          :service="kASService"
                          :columns="kASColumns"
                          :fastFilters="[]"
                          :defaultFilter="{ disabled: { and: [false] } }"
                          :invalid="v$.model.kasValue.$error"
                          :invalid-messages="v$.model.kasValue.$errors"
                          :grid-styles="searchFieldGridStyles"
                          @blur="v$.model.kasValue.$touch"
                        />
                        <ProSpaceSearchfield
                          ref="merchandiserValue-ProSpaceSearchfield"
                          v-model="model.merchandiserValue"
                          getMethod="grid"
                          optionValue="value"
                          optionLabel="value"
                          filterModel="value"
                          :displayValue="model.merchandiserValue"
                          :label="$t('addressProgramOrderMerchandiserValue')"
                          :txtTitleModal="$t('addressProgramOrderMerchandiserValue')"
                          :searchPlaceholder="
                            adjustmentFields.includes('merchandiserValue')
                              ? $t('addressProgramOrderAdjustmentNewValue')
                              : $t('addressProgramOrderMerchandiserValue')
                          "
                          :applyTxt="$t('applyTxt')"
                          :cancelTxt="$t('cancelTxt')"
                          :service="merchandiserService"
                          :columns="merchandiserColumns"
                          :fastFilters="[]"
                          :defaultFilter="{ disabled: { and: [false] } }"
                          :invalid="v$.model.merchandiserValue.$error"
                          :invalid-messages="v$.model.merchandiserValue.$errors"
                          :grid-styles="searchFieldGridStyles"
                          @blur="v$.model.merchandiserValue.$touch"
                        />
                        <ProSpaceSearchfield
                          ref="routeCodeValue-ProSpaceSearchfield"
                          v-model="model.routeCodeValue"
                          getMethod="grid"
                          optionValue="value"
                          optionLabel="value"
                          filterModel="value"
                          :displayValue="model.routeCodeValue"
                          :label="$t('addressProgramOrderRouteCodeValue')"
                          :txtTitleModal="$t('addressProgramOrderRouteCodeValue')"
                          :searchPlaceholder="
                            adjustmentFields.includes('routeCodeValue')
                              ? $t('addressProgramOrderAdjustmentNewValue')
                              : $t('addressProgramOrderRouteCodeValue')
                          "
                          :applyTxt="$t('applyTxt')"
                          :cancelTxt="$t('cancelTxt')"
                          :service="routeCodeService"
                          :columns="routeCodeColumns"
                          :fastFilters="[]"
                          :defaultFilter="{ disabled: { and: [false] } }"
                          :invalid="v$.model.routeCodeValue.$error"
                          :invalid-messages="v$.model.routeCodeValue.$errors"
                          :grid-styles="searchFieldGridStyles"
                          @blur="v$.model.routeCodeValue.$touch"
                        />
                      </ProSpaceHLayout>
                    </ProSpaceVLayout>
                  </template>

                  <template
                    v-if="
                      v$.model.kasValue.$error ||
                      v$.model.merchandiserValue.$error ||
                      v$.model.routeCodeValue.$error
                    "
                    v-slot:right
                  >
                    <ProSpaceVLayout justify-content="center">
                      <ProSpaceIcon icon="warning" colored />
                    </ProSpaceVLayout>
                  </template>
                </ProSpaceGeoLayout>
                <ProSpaceGeoLayout
                  class="geo-row"
                  isHideBottom
                  isHideTop
                  :isHideRight="
                    !v$.model.ratePerHourPlan.$error &&
                    !v$.model.frequencyPlan.$error &&
                    !v$.model.timingPerVisitPlan.$error &&
                    !v$.model.fteMerchPlan.$errors
                  "
                >
                  <template v-slot:left>
                    <ProSpaceTextBlock
                      :top="$t('openKpi')"
                      :bottom="$t('openKpi')"
                      :gap="10"
                      break-word
                    />
                  </template>
                  <template v-slot:center>
                    <ProSpaceVLayout :gap="20">
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceInputNumber
                          ref="ratePerHourPlan-ProSpaceInputNumber"
                          v-model="model.ratePerHourPlan"
                          :integeronly="false"
                          :minFractionDigits="2"
                          :maxFractionDigits="2"
                          :max="100000"
                          :label="$t('addressProgramOrderRatePerHour')"
                          :placeholder="
                            adjustmentFields.includes('ratePerHourPlan')
                              ? $t('addressProgramOrderAdjustmentNewValue')
                              : $t('addressProgramOrderRatePerHour')
                          "
                          :invalid="v$.model.ratePerHourPlan.$error"
                          :invalid-messages="v$.model.ratePerHourPlan.$errors"
                          likeText
                          @blur="v$.model.ratePerHourPlan.$touch"
                        />
                        <ProSpaceInputNumber
                          ref="frequencyPlan-ProSpaceInputNumber"
                          v-model="model.frequencyPlan"
                          :integeronly="true"
                          :label="$t('addressProgramOrderFrequency')"
                          :placeholder="
                            adjustmentFields.includes('frequencyPlan')
                              ? $t('addressProgramOrderAdjustmentNewValue')
                              : $t('addressProgramOrderFrequency')
                          "
                          :invalid="v$.model.frequencyPlan.$error"
                          :invalid-messages="v$.model.frequencyPlan.$errors"
                          likeText
                          @blur="v$.model.frequencyPlan.$touch"
                        />
                        <ProSpaceInputNumber
                          ref="timingPerVisitPlan-ProSpaceInputNumber"
                          v-model="model.timingPerVisitPlan"
                          :integeronly="false"
                          :minFractionDigits="2"
                          :maxFractionDigits="2"
                          :label="$t('timingPerVisit')"
                          :placeholder="
                            adjustmentFields.includes('timingPerVisitPlan')
                              ? $t('addressProgramOrderAdjustmentNewValue')
                              : $t('timingPerVisit')
                          "
                          :invalid="v$.model.timingPerVisitPlan.$error"
                          :invalid-messages="
                            v$.model.timingPerVisitPlan.$errors
                          "
                          likeText
                          @blur="v$.model.timingPerVisitPlan.$touch"
                        />
                      </ProSpaceHLayout>
                      <ProSpaceHLayout
                        justify-content="flex-start"
                        align-items="flex-start"
                        :gap="20"
                      >
                        <ProSpaceInputNumber
                          ref="fteMerchPlan-ProSpaceInputNumber"
                          v-model="model.fteMerchPlan"
                          :integeronly="false"
                          :maxFractionDigits="2"
                          :label="$t('addressProgramOrderFteMerch')"
                          :placeholder="$t('addressProgramOrderFteMerch')"
                          :invalid="v$.model.fteMerchPlan.$error"
                          :invalid-messages="v$.model.fteMerchPlan.$errors"
                          likeText
                          @blur="v$.model.fteMerchPlan.$touch"
                        />
                      </ProSpaceHLayout>
                    </ProSpaceVLayout>
                  </template>

                  <template
                    v-if="
                      v$.model.ratePerHourPlan.$error ||
                      !v$.model.frequencyPlan.$error ||
                      !v$.model.timingPerVisitPlan.$error ||
                      !v$.model.fteMerchPlan.$errors
                    "
                    v-slot:right
                  >
                    <ProSpaceVLayout justify-content="center">
                      <ProSpaceIcon icon="warning" colored />
                    </ProSpaceVLayout>
                  </template>
                </ProSpaceGeoLayout>
                <ProSpaceGeoLayout
                  v-if="adjustmentMode && !disableCommentInTabParams"
                  class="geo-row"
                  isHideBottom
                  isHideTop
                  :isHideRight="!v$.model.comment.$error"
                >
                  <template v-slot:left>
                    <ProSpaceTextBlock
                      :top="$t('addressProgramCommentInfo')"
                      :bottom="$t('addressProgramCommentDescription')"
                      :gap="10"
                      break-word
                    />
                  </template>
                  <template v-slot:center>
                    <ProSpaceHLayout
                      justify-content="flex-start"
                      align-items="flex-start"
                      :gap="20"
                    >
                      <ProSpaceInputText
                        ref="comments-ProSpaceInputText-alt"
                        class="addressprogramorder__input-textarea"
                        v-model="model.comment"
                        isTextarea
                        :label="$t('addressProgramOrderComment')"
                        :placeholder="$t('addressProgramOrderComment')"
                        :invalid="v$.model.comment.$error"
                        :invalid-messages="v$.model.comment.$errors"
                        @blur="v$.model.comment.$touch"
                      />
                    </ProSpaceHLayout>
                  </template>

                  <template v-if="v$.model.comment.$error" v-slot:right>
                    <ProSpaceVLayout justify-content="center">
                      <ProSpaceIcon icon="warning" colored />
                    </ProSpaceVLayout>
                  </template>
                </ProSpaceGeoLayout>
              </ProSpaceVLayout>
            </template>
            <template v-slot:tab-2>
              <AddressProgramOrderTabProto
                ref="tabKPI"
                :title="$t('businessKpi')"
                :description="$t('addressProgramKPIDescription')"
                :fields="businessKPIFields"
                :v$="v$"
                :import-model="model"
                :adjustment-fields="adjustmentFields"
                :adjustment-mode="adjustmentMode"
                :access="access"
                :user-info="userinfo"
                :orderStatus="orderStatus"
                :needCheckOrderStatusForMountAccess="needCheckOrderStatusForMountAccess"
                :enabled-comments="!disableCommentInTabKPI"
                :isMultiAdjustmentMode="this.isMultiAdjustmentMode"
                @update:model="updateModelFromTabKPI"
                @access:loaded="tabAccessLoaded('kpi', $event)"
              />
            </template>
            <template v-slot:tab-3>
              <AddressProgramOrderTabProto
                ref="tabBudget"
                :title="$t('addressProgramBudgetInfo')"
                :description="$t('addressProgramBudgetDescription')"
                :fields="budgetFields"
                :v$="v$"
                :import-model="model"
                :adjustment-fields="adjustmentFields"
                :adjustment-mode="adjustmentMode"
                :access="access"
                :user-info="userinfo"
                :orderStatus="orderStatus"
                :enabled-comments="false"
                :isMultiAdjustmentMode="this.isMultiAdjustmentMode"
                @update:model="updateModelFromTabKPI"
                @access:loaded="tabAccessLoaded('budget', $event)"
              />
            </template>
            <template v-slot:tab-4>
              <AdjustmentOutletsGrid
                :service="addressProgramOrderService"
                :defaultFilter="adjustmentOutletFilterObject || adjustmentOutletFilter"
                get-method="grid"
                :check-access="checkAccess"
              />
            </template>
          </ProSpaceTabs>
        </template>
      </ProSpaceGeoLayout>
    </template>
  </ProSpaceGeoLayout>
  <ProSpaceFlashMessage
    v-model="showBlockAdjustmentsModal"
    :cancelTxt="$t('closeTxt')"
    :title="$t('adjustmentsAreBlocked')"
    icon="flash-warning"
    @cancel="showBlockAdjustmentsModal = false"
  >
    {{ $t('adjustmentAreBlockedTextInfo') }}
  </ProSpaceFlashMessage>
</template>
<script>
import {
  ProSpaceTopPanel,
  ProSpaceInputText,
  ProSpaceAction,
  ProSpaceRadioButtonSecondary,
  ProSpaceGeoLayout,
  ProSpacePanelBlock,
  ProSpacePanelBlockList,
  ProSpaceRightPanel,
  ProSpaceTabs,
  ProSpaceTextBlock,
  ProSpaceHLayout,
  ProSpaceVLayout,
  ProSpaceSearchfield,
  ProSpaceFile,
  ProSpaceInputCalendar,
  ProSpaceInputNumber,
  ProSpaceMultiSelect,
  ProSpaceCustomFilterPanel,
  ProSpaceIcon,
  ProSpaceInputSwitch,
  ProSpaceLabel,
  ProSpaceTag,
  ProSpaceFlashMessage,
  ProSpaceDropdown,
} from "@prospace/prospace-components-library";
import formatDigit from "@prospace/prospace-components-library/src/lib-assets/mixins/digitFormatter";
import { AddressProgramOrderService } from "../../services/AddressProgramOrderService.js";
import { HSRService } from "../../services/HSRService.js";
import { TDMService } from "../../services/TDMService.js";
import { KADLService } from "../../services/KADLService.js";
import { AgencyService } from "../../services/AgencyService.js";
import { NetworkService } from "../../services/NetworkService.js";
import { ExternalFormatService } from "../../services/ExternalFormatService.js";
import { OutletTypeService } from "../../services/OutletTypeService.js";
import { ChannelService } from "../../services/ChannelService.js";
import { ClusterService } from "../../services/ClusterService.js";
import { NARKAService } from "../../services/NARKAService.js";
import { SIPChannelService } from "../../services/SIPChannelService.js";
import { KASService } from "../../services/KASService.js";
import { MerchandiserService } from "../../services/MerchandiserService.js";
import { RouteCodeService } from "../../services/RouteCodeService.js";
import { AdjustmentService } from "../../services/AdjustmentService.js";
import { mapGetters } from "vuex";
import accessMixin from "../../../../../Frontends/Common/utils/elementAccess/accessMixin";
import _ from "lodash";
import { helpers, minValue, createI18nMessage } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import AdjustmentOutletsGrid from "../Adjustments/components/AdjustmentOutletsGrid.vue";
import AddressProgramOrderTabProto from "./components/AddressProgramOrderTabProto.vue";
// import tempService from "./temp.js";
export default {
  mixins: [accessMixin],
  props: ['id', 'adjustmentSettings'],
  components: {
    AddressProgramOrderTabProto,
    AdjustmentOutletsGrid,
    ProSpaceTopPanel,
    ProSpaceGeoLayout,
    ProSpaceInputText,
    ProSpaceAction,
    ProSpaceRadioButtonSecondary,
    ProSpacePanelBlock,
    ProSpacePanelBlockList,
    ProSpaceRightPanel,
    ProSpaceTabs,
    ProSpaceTextBlock,
    ProSpaceHLayout,
    ProSpaceVLayout,
    ProSpaceSearchfield,
    ProSpaceFile,
    ProSpaceInputCalendar,
    ProSpaceInputNumber,
    ProSpaceMultiSelect,
    ProSpaceCustomFilterPanel,
    ProSpaceIcon,
    ProSpaceInputSwitch,
    ProSpaceLabel,
    ProSpaceTag,
    ProSpaceFlashMessage,
    ProSpaceDropdown
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    let defaultSearchFieldColumns = [
      { field: "code", header: this.$t("code") },
    ];

    let networkColumns = [
      ...defaultSearchFieldColumns,
      { field: "value", header: this.$t("networkValue") },
    ];
    let clusterColumns = [
      ...defaultSearchFieldColumns,
      { field: "value", header: this.$t("clusterValue") },
    ];
    let hSRColumns = [
      ...defaultSearchFieldColumns,
      { field: "value", header: this.$t("hSRValue") },
    ];
    let tDMColumns = [
      ...defaultSearchFieldColumns,
      { field: "value", header: this.$t("tDMValue") },
    ];
    let kADLColumns = [
      ...defaultSearchFieldColumns,
      { field: "value", header: this.$t("kADLValue") },
    ];
    let externalFormatColumns = [
      ...defaultSearchFieldColumns,
      { field: "value", header: this.$t("externalFormatValue") },
    ];
    let outletTypeColumns = [
      ...defaultSearchFieldColumns,
      { field: "value", header: this.$t("outletTypeValue") },
    ];
    let channelColumns = [
      ...defaultSearchFieldColumns,
      { field: "value", header: this.$t("channelValue") },
    ];
    let nARKAColumns = [
      ...defaultSearchFieldColumns,
      { field: "value", header: this.$t("nARKAValue") },
    ];
    let sIPChannelColumns = [
      ...defaultSearchFieldColumns,
      { field: "value", header: this.$t("sIPChannelValue") },
    ];
    let territoryClusterColumns = [
      ...defaultSearchFieldColumns,
      { field: "value", header: this.$t("territoryClusterValue") },
    ];
    let agencyColumns = [
      ...defaultSearchFieldColumns,
      { field: "value", header: this.$t("agencyValue") },
    ];
    let kASColumns = [
      ...defaultSearchFieldColumns,
      { field: "value", header: this.$t("kASValue") },
    ];
    let merchandiserColumns = [
      ...defaultSearchFieldColumns,
      { field: "value", header: this.$t("merchandiserValue") },
    ];
    let routeCodeColumns = [
      ...defaultSearchFieldColumns,
      { field: "value", header: this.$t("routeCodeValue") },
    ];

    return {
      // Adjustment
      adjustmentMode: false,
      adjustmentOutletFilter: null,
      adjustmentOutletFilterObject: null,
      addressProgramOrdersToAdjust: [],
      groupingState: null,
      orderStatus: null,
      isPlanFact: null,

      // Tabs
      modelTab: {
        name: this.$t("parameters"),
        systemName: "parameters",
        isActive: true,
      },

      // Common
      layoutName: "",
      model: null,
      compareModel: null,
      loading: false,

      // Service
      addressProgramOrderService: new AddressProgramOrderService(),
      hSRService: new HSRService(),
      tDMService: new TDMService(),
      kADLService: new KADLService(),
      agencyService: new AgencyService(),
      networkService: new NetworkService(),
      externalFormatService: new ExternalFormatService(),
      outletTypeService: new OutletTypeService(),
      channelService: new ChannelService(),
      clusterService: new ClusterService(),
      nARKAService: new NARKAService(),
      sIPChannelService: new SIPChannelService(),
      kASService: new KASService(),
      merchandiserService: new MerchandiserService(),
      routeCodeService: new RouteCodeService(),
      adjustmentService: new AdjustmentService(),

      getMethod: "get",
      masterId: null,

      // SearchField
      networkColumns: networkColumns,
      clusterColumns: clusterColumns,
      hSRColumns: hSRColumns,
      tDMColumns: tDMColumns,
      kADLColumns: kADLColumns,
      externalFormatColumns: externalFormatColumns,
      outletTypeColumns: outletTypeColumns,
      channelColumns: channelColumns,
      nARKAColumns: nARKAColumns,
      sIPChannelColumns: sIPChannelColumns,
      territoryClusterColumns: territoryClusterColumns,
      agencyColumns: agencyColumns,
      kASColumns: kASColumns,
      merchandiserColumns: merchandiserColumns,
      routeCodeColumns: routeCodeColumns,

      prefixTop: "",
      prefixBottom: "",

      groupCounter: null,
      agencyId: null,
      showBlockAdjustmentsModal: false,
      kpiAccessLoading: false,
      budgetAccessLoading: false,
      disableCommentInTabKPI: true,
      disableCommentInTabParams: true,
    };
  },
  validations() {
    const withI18nMessage = createI18nMessage({ t: this.$t });
    const localMinValue = withI18nMessage(minValue(this.model?.coverage? 1 : 0), { withArguments: true });

    return {
      model: {
        marsPeriod: {},
        outletId: {},
        area: {},
        networkCode: {},
        externalCode: {},
        city: {},
        address: {},
        coverage: {},
        coverageComments: {},
        comment: {},
        hsrId: {},
        tdmId: {},
        kadlId: {},
        agencyId: {},
        networkId: {},
        externalFormatId: {},
        outletTypeId: {},
        channelId: {},
        clusterId: {},
        narkaId: {},
        sipChannelId: {},
        kasValue: {},
        merchandiserValue: {},
        routeCodeValue: {},
        tariffZone: {},
        territoryCluster: {},
        skyline: {},
        ratePerHourPlan: {},
        timingPerVisitPlan: {},
        fteMerchPlan: {},
        frequencyPlan: !this.isMultiAdjustmentMode ? {
          localMinValue
        } : {},
        outletCount: {},
        osaPlan: {},
        deltaISAOSAPlan: {},
        visitSumPlan: {},
        osaSumPlan: {},
        budgetTotalPlan: {},
        pogWetWHSPlan: {},
        pogWetPFTPlan: {},
        pogWetSHBPlan: {},
        pogDryWHSPlan: {},
        pogDryPFTPlan: {},
        lSizePlan: {},
        stripeCTDreamPlan: {},
        stripePFTCatPlan: {},
        stripePFDogPlan: {},
        stripeSPPlan: {},
        ctShareDreamPlan: {},
        ctSharePFCatPlan: {},
        ctSharePFDogPlan: {},
        exSite: {},
        hotZone: {},
        budgetVisitPlan: {},
        budgetOSAPlan: {},
        budgetDeltaISAOSAPlan: {},
        budgetPOGWetWHSPlan: {},
        budgetPOGWetPFTPlan: {},
        budgetPOGWetSHBPlan: {},
        budgetPOGDryWHSPlan: {},
        budgetPOGDryPFTPlan: {},
        budgetLSizePlan: {},
        budgetStripeCTDreamPlan: {},
        budgetStripePFTCatPlan: {},
        budgetStripePFDogPlan: {},
        budgetStripeSPPlan: {},
        budgetCTShareDreamPlan: {},
        budgetCTSharePFCatPlan: {},
        budgetCTSharePFDogPlan: {},
        budgetExSitePlan: {},
        budgetHotZonePlan: {},
      },
    };
  },
  beforeRouteLeave() {
    if(this.$route.query.settingsCode)
      localStorage.removeItem(this.$route.query.settingsCode);
  },
  created() {
    this.masterId = this.$route.params.id;
    const settingsCode = this.$route.query.settingsCode;

    if (settingsCode && atob(settingsCode).includes(this.userinfo.id)) {
      let stringSettings = localStorage.getItem(settingsCode);
      let settings = stringSettings ? JSON.parse(stringSettings) : null;
      if (settings) {
        if (settings.adjustmentSettings) {
          this.adjustmentMode = true;
          this.addressProgramOrdersToAdjust = settings.adjustmentSettings.records;
          this.adjustmentOutletFilter = settings.adjustmentSettings.filter;
          this.adjustmentOutletFilterObject = settings.adjustmentSettings.filterObject;
          this.groupCounter = settings.adjustmentSettings.groupCounter;
        }
        if (settings.groupingState) {
          this.groupingState = settings.groupingState
        }
      }
    }
  },
  mounted() {
    this.loadRoleAccess('addressprogramorder');
    this.getData()
      .then(() => this.mountAccess('addressprogramorder', true, this.needCheckOrderStatusForMountAccess))
      .finally(() => {
        this.setCommentsCondition();
      });
  },
  methods: {
    formatDigit,
    onSelectTab(tab) {
      if (tab.selectedTab.systemName === "parameters") {
        this.mountAccess('addressprogramorder', true, this.needCheckOrderStatusForMountAccess)
      } else if (tab.selectedTab.systemName === "businessKPI") {
        this.kpiAccessLoading = true;
      } else if (tab.selectedTab.systemName === "budget") {
        this.budgetAccessLoading = true;
      }
    },
    async getData() {
      let result = {};

      if (this.isMultiAdjustmentMode) {
        result["data"] = {
          code: null,
          address: null,
          territoryCluster: null,
          externalCode: null,
          tariffZone: null,
          skyline: null,
          frequencyPlan: null,
          timingPerVisitPlan: null,
          fteMerchPlan: null,
          osaPlan: null,
          deltaISAOSAPlan: null,
          ratePerHourPlan: null,
          pogWetWHSPlan: null,
          pogWetPFTPlan: null,
          pogWetSHBPlan: null,
          pogDryWHSPlan: null,
          pogDryPFTPlan: null,
          lSizePlan: null,
          stripeCTDreamPlan: null,
          stripePFTCatPlan: null,
          stripePFDogPlan: null,
          stripeSPPlan: null,
          ctShareDreamPlan: null,
          ctSharePFCatPlan: null,
          ctSharePFDogPlan: null,
          exSite: null,
          hotZone: null,
          budgetDeltaISAOSAPlan: null,
          budgetPOGWetWHSPlan: null,
          budgetPOGWetPFTPlan: null,
          budgetPOGWetSHBPlan: null,
          budgetPOGDryWHSPlan: null,
          budgetPOGDryPFTPlan: null,
          budgetLSizePlan: null,
          budgetStripeCTDreamPlan: null,
          budgetStripePFTCatPlan: null,
          budgetStripePFDogPlan: null,
          budgetStripeSPPlan: null,
          budgetCTShareDreamPlan: null,
          budgetCTSharePFCatPlan: null,
          budgetCTSharePFDogPlan: null,
          budgetExSitePlan: null,
          budgetHotZonePlan: null,
          budgetVisitPlan: null,
          budgetOsaPlan: null,
          budgetTotalPlan: null,
          coverage: null
        };

        if (this.$route.query.adjustmentSettings) {
          let settings = JSON.parse(this.$route.query.adjustmentSettings);
          result["data"].agencyId = settings.agencyId;
          result["data"].agencyValue = settings.agencyValue;
        } else if (this.agencyId) {
          result["data"].agencyId = this.agencyId;
          result["data"].agencyValue = this.agencyValue;
        }

        this.adjustmentFields.forEach((key) => {
          result["data"][key] = null;
        });

        this.layoutName = `${this.$t("adjustmentGroupTT")} | ${this.$t("adjustmentRequest")}`;

        if (this.addressProgramOrdersToAdjust?.length) {
          this.orderStatus = this.addressProgramOrdersToAdjust?.at(0)?.orderStatus
          this.isPlanFact = this.addressProgramOrdersToAdjust.every(apo => apo.status === 'planfact')
        }
        else {
          const orderResult = await this.addressProgramOrderService.grid(this.adjustmentOutletFilter)
          this.orderStatus = orderResult?.data?.records?.at(0)?.orderStatus
          this.isPlanFact = orderResult?.data?.records?.every(apo => apo.status === 'planfact')
        }
      } else {
        if (this.masterId === undefined) return;

        result = await this.addressProgramOrderService[this.getMethod](this.masterId);

        if (result?.data) {
          this.layoutName = result.data.networkValue;
          this.orderStatus = result.data.orderStatus;
          this.isPlanFact = result.data.status === 'planfact'
          if (this.adjustmentMode)
            this.layoutName += ` | ${this.$t("adjustmentRequest")}`;
        }
      }

      if (result?.data) {
        this.model = result.data;

        if (!this.isMultiAdjustmentMode) {
          this.model.osaPlan = this.model.osaPlan ? this.model.osaPlan * 100 : 0;
          this.model.deltaISAOSAPlan = this.model.deltaISAOSAPlan ? this.model.deltaISAOSAPlan * 100 : 0;
        }
        this.compareModel = _.cloneDeep(this.model);
      }
    },
    save(needRefresh = true, needBack = false) {
      this.loading = true;
      const modelClone = _.clone(this.model)
      modelClone.osaPlan = modelClone.osaPlan ? modelClone.osaPlan / 100 : 0;
      modelClone.deltaISAOSAPlan = modelClone.deltaISAOSAPlan ? modelClone.deltaISAOSAPlan / 100 : 0;

      this.addressProgramOrderService.update(modelClone).then(async ({ data }) => {
        if (needRefresh) {
          await this.getData();
        }
        if (data && data.length > 0) {
          this.model.stamp = data[0].stamp;
        }

        if (needBack) this.backHandler();

        this.loading = false;
      })
        .catch(() => {
          this.loading = false;
        });
    },
    saveAndClose() {
      this.save(false, true);
    },
    sendAndClose() {
      this.loading = true;
      let adjustmentModel = this.buildAdjustmentModel();
      this.adjustmentService.create(adjustmentModel).then(() => {
        this.backHandler();
        this.loading = false;
      })
        .catch(() => {
          this.loading = false;
        });
    },
    buildAdjustmentModel() {
      let me = this;

      let adjustmentModel = {
        addressProgramOrders: me.isMultiAdjustmentMode? me.addressProgramOrdersToAdjust : [ me.model ]
      };
      let editedFields = [];

      let getLabelCode = function(key) {
        let labelCode = "addressProgramOrder" + key[0].toUpperCase() + key.slice(1);
        if(labelCode.includes("Plan") && me.modelTab.systemName === 'parameters')
          labelCode = labelCode.slice(0, -4);

        if(labelCode.includes("Id"))
          labelCode = labelCode.slice(0, -2) + "Value";

        return labelCode;
      }

      const prevLocale = me.$i18n.locale;
      me.$i18n.locale = "ru";

      me.adjustmentFields.forEach((key) => {
        let mVal = me.model[key] === undefined ? null : me.model[key];
        let cVal = me.compareModel[key] === undefined ? null : me.compareModel[key];
        if (mVal !== cVal) {
          let displayValue = null;
          let oldDisplayValue = null;
          if(key === "osaPlan" || key === "deltaISAOSAPlan") {
            displayValue = me.model[key].toFixed(2);
            oldDisplayValue = me.compareModel[key];
            me.model[key] /= 100;
          }
          else if(key.includes("Id")){
            let keyValue = key.slice(0, -2) + "Value";
            displayValue = me.model[keyValue];
            oldDisplayValue = me.compareModel[keyValue];
          }

          editedFields.push({
            key: key,
            name: me.$t(getLabelCode(key)),
            oldDisplayValue: oldDisplayValue,
            displayValue: displayValue,
            oldValue: me.compareModel[key],
            newValue: me.model[key],
            agreementStatus: null,
            approveStatus: null,
          });
        }
      });
      me.$i18n.locale = prevLocale;

      let filter = me.isMultiAdjustmentMode
        ? me.adjustmentOutletFilter
        : null;

      adjustmentModel["adjustmentComment"] = me.model.comment;
      adjustmentModel["securityRoleSystemName"] = JSON.parse(localStorage.getItem("userRole")).systemName;
      adjustmentModel["addressProgramOrderFilter"] = filter;
      adjustmentModel["editedFields"] = editedFields
        .map((x) => x.name)
        .join(", ");
      adjustmentModel["editedFieldsConfig"] = JSON.stringify(editedFields);

      return adjustmentModel;
    },
    submitFilter(nameRef = "grid") {
      if (!this.$refs[nameRef]) return;
      this.$refs[nameRef].getRecords(true);
    },
    backHandler() {
      if (this.groupingState) {
        this.$router.push({
          name: 'addressprogramorders',
          params: {
            groupingState: JSON.stringify(this.groupingState)
          }
        })
        return
      }
      this.$router.go(-1);
    },
    cancelHandler() {
      if (this.adjustmentMode && !this.isMultiAdjustmentMode)
        this.toggleAdjustmentMode();
      else this.backHandler();
    },
    toggleAdjustmentMode() {
      if (this.currentPeriod?.isBlocked) {
        this.showBlockAdjustmentsModal = true;
        return;
      }
      this.adjustmentMode = !this.adjustmentMode;
      this.clearAllCondition();
      this.$nextTick(() => {
        if (this.modelTab.systemName === "businessKPI") {
          this.kpiAccessLoading = true;
        } else if (this.modelTab.systemName === "budget") {
          this.budgetAccessLoading = true;
        }
        this.getTabContext.refreshMountAccess();
        this.getData();
      });
    },
    refreshMountAccess() {
      this.getTabContext.mountAccess('addressprogramorder', true, this.needCheckOrderStatusForMountAccess);
      this.setCommentsCondition();
    },
    changeModelValue(option, refName) {
      this.model[option] = this.$refs[refName + "-ProSpaceSearchfield"].modelInput;
    },
    updateModelFromTabKPI(kpi) {
      this.model = { ...this.model, ...kpi };
    },
    tabAccessLoaded(name, state) {
      if (!state) {
        this[`${name}AccessLoading`] = state;
      } else {
        this.setCommentsCondition();
      }
    },
    setCommentsCondition() {
      if (this.adjustmentMode) {
        const firstStep = this.orderStatus === 'published' && !this.isPlanFact;
        const secondStep = this.orderStatus === 'approved' && this.isPlanFact;

        this.disableCommentInTabParams = true;
        this.disableCommentInTabKPI = true;

        this.disableCommentInTabParams = !firstStep;
        this.disableCommentInTabKPI = !secondStep;
      }
    }
  },
  computed: {
    ...mapGetters({
      access: "securityRoleAccess/access",
      userinfo: "userinfoStore/getUserinfo",
      currentPeriod: "marsPeriodStore/getMarsPeriod",
    }),
    // access() {
    //   return tempService;
    // },
    coverageOptions() {
      const options = [
        {label: this.$t('yes'), value: true},
        {label: this.$t('no'), value: false},
      ]
      if (this.isMultiAdjustmentMode) {
        options.push({
          label: this.$t('noSeletValue'), value: null
        })
      }
      return options
    },
    adjustmentFields() {
      let fields = [];
      if (this.adjustmentMode) {
        this.refsForCheckInMixin.forEach((ref) => {
          if (
            !ref.includes("action-") &&
            !ref.includes("comments-") &&
            this.checkRoleAccess(ref, null, this.access, true, true)
          )
            fields.push(ref.split("-")[0]);
        });
        if (this.$refs['tabKPI'])
          Object.keys(this.$refs['tabKPI'].$refs).forEach((ref) => {
            if (
              !ref.includes("action-") &&
              !ref.includes("comments-") &&
              this.checkRoleAccess(ref, null, this.access, true, true)
            )
              fields.push(ref.split("-")[0]);
          });
      }

      return fields;
    },
    refsForCheckInMixin() {
      let refs = [];
      let fields = this[`${this.modelTab.systemName}Fields`];
      refs.push(
        "action-cancel",
        ...fields
      );
      if (!this.adjustmentMode) {
        refs.push(
          "agencyId-ProSpaceSearchfield",
          "action-save",
          "action-saveAndClose"
        );

        if(this.model && this.model?.orderStatus !== 'completed' && this.needCheckOrderStatusForMountAccess)
          refs.push(
            "action-adjustmentRequest",
          );
      }
      // else refs.push("comments-ProSpaceInputText");

      return refs;
    },
    isMultiAdjustmentMode() {
      return (this.adjustmentMode && this.groupCounter && this.groupCounter > 1) || this.$route.name === 'adjustmentmultiplecreate';
    },
    isCorrectionChanges() {
      return Object.keys(this.model).some(key => {
          let mVal = this.model[key];
          let cVal = this.compareModel[key];
          return key == "comment"? false : mVal != cVal;
      });
    },
    overviewTabs() {
      return [
        {
          name: this.$t("parameters"),
          systemName: "parameters",
          isActive: this.modelTab.systemName === "parameters",
        },
        {
          name: this.$t("businessKpi"),
          systemName: "businessKPI",
          isActive: this.modelTab.systemName === "businessKPI",
        },
        {
          name: this.$t("budget"),
          systemName: "budget",
          isActive: this.modelTab.systemName === "budget",
        },
        {
          name: this.$t("outlets"),
          systemName: "outlets",
          isActive: this.modelTab.systemName === "outlets",
          isHidden: !this.isMultiAdjustmentMode,
          counter: this.groupCounter
        },
      ];
    },
    headerTag() {
      return {
        prefix: this.$t('addressProgramOrderOutletId'),
        text: this.model?.outletId,
        type: 'gray-light'
      }
    },
    searchFieldGridStyles() {
      return {
        height: "50vh",
        width: "30vw",
      }
    },
    parametersFields() {
      return [
        "networkId-ProSpaceSearchfield",
        "address-ProSpaceInputText",
        "coverage-ProSpaceInputSwitch",
        "area-ProSpaceInputText",
        "city-ProSpaceInputText",
        "clusterId-ProSpaceSearchfield",
        "hsrId-ProSpaceSearchfield",
        "tdmId-ProSpaceSearchfield",
        "kadlId-ProSpaceSearchfield",
        "externalFormatId-ProSpaceSearchfield",
        "outletTypeId-ProSpaceSearchfield",
        "channelId-ProSpaceSearchfield",
        "narkaId-ProSpaceSearchfield",
        "sipChannelId-ProSpaceSearchfield",
        "territoryCluster-ProSpaceInputNumber",
        "externalCode-ProSpaceInputText",
        "tariffZone-ProSpaceInputNumber",
        "skyline-ProSpaceInputNumber",
        "kasValue-ProSpaceSearchfield",
        "merchandiserValue-ProSpaceSearchfield",
        "routeCodeValue-ProSpaceSearchfield",
        "frequencyPlan-ProSpaceInputNumber",
        "timingPerVisitPlan-ProSpaceInputNumber",
        "fteMerchPlan-ProSpaceInputNumber",
        "ratePerHourPlan-ProSpaceInputNumber"
      ];
    },
    businessKPIFields() {
      return [
        'osaPlan',
        'deltaISAOSAPlan',
        'pogWetWHSPlan',
        'pogWetPFTPlan',
        'pogWetSHBPlan',
        'pogDryWHSPlan',
        'pogDryPFTPlan',
        'lSizePlan', // представленность Марс
        'stripeCTDreamPlan',
        'stripePFTCatPlan',
        'stripePFDogPlan',
        'stripeSPPlan',
        'ctShareDreamPlan',
        'ctSharePFCatPlan',
        'ctSharePFDogPlan',
        'exSitePlan', // ДМП Grocery
        'hotZonePlan' // Прилегающая к кассам зона SPT
      ];
    },
    budgetFields() {
      return [
        'budgetVisitPlan',
        'budgetOSAPlan',
        'budgetDeltaISAOSAPlan',
        'budgetPOGWetWHSPlan',
        'budgetPOGWetPFTPlan',
        'budgetPOGWetSHBPlan',
        'budgetPOGDryWHSPlan',
        'budgetPOGDryPFTPlan',
        'budgetLSizePlan',
        'budgetStripeCTDreamPlan',
        'budgetStripePFTCatPlan',
        'budgetStripePFDogPlan',
        'budgetStripeSPPlan',
        'budgetCTShareDreamPlan',
        'budgetCTSharePFCatPlan',
        'budgetCTSharePFDogPlan',
        'budgetExSitePlan',
        'budgetHotZonePlan',
        'totalSumPlan'
      ];
    },
    getRefLoading() {
      return (this.mixinLoadingAccess && this.modelTab.systemName === 'parameters') ||
        (this.kpiAccessLoading && this.modelTab.systemName === 'businessKPI') ||
        (this.budgetAccessLoading && this.modelTab.systemName === 'budget')
    },
    getTabContext() {
      const tab = this.modelTab.systemName;
      if (tab === 'parameters') {
        return this;
      } else if (tab === 'businessKPI') {
        return this.$refs.tabKPI;
      } else if (tab === 'budget') {
        return this.$refs.tabBudget;
      }
    },
    needCheckOrderStatusForMountAccess() {
      return this.orderStatus === 'published' || this.orderStatus === 'approved' && this.isPlanFact
    }
  },
};
</script>
<style lang="scss">
.addressprogramorder {
  &__rows {
    & > .geo-row {
      grid-template-columns: 220px minmax(306px, 958px) !important;
      grid-template-areas: "left center"; // set "left center right" when will need show right-col
      width: auto;
    }
  }
  .prospace-input-layout {
    max-width: 306px;
  }
  &__input-textarea {
    max-width: 633px !important;
  }

  .prospace-text-block__bottom {
    color: var(--prospace-ui-icon-gray);
  }

  &__outlets {
    .prospace-tabs {
      & > .prospace-scrollbar {
        overflow: hidden !important;
      }
    }

    td.address-program__column-store .store-column__single {
      height: 100% !important;
      border-right: 1px dashed var(--prospace-ui-border-color);

      .prospace-h-layout {
        height: auto !important;
      }
    }
  }
}
</style>
